import { Injectable, Inject } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { filter } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { SeoData } from '../shared/interfaces/seo.types';

@Injectable({ providedIn: 'root' })
export class SeoService {
  private canonicalEl!: HTMLLinkElement;
  private jsonLdEl: HTMLScriptElement | null = null;

  constructor(
    private titleSrv: Title,
    private meta: Meta,
    private router: Router,
    private route: ActivatedRoute,
    @Inject(DOCUMENT) private doc: Document
  ) {
    // Aplica en cada navegación SPA
    this.router.events.pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => this.applyFromRoute());
    // Aplica en la primera carga (SSR/prerender/browser)
    this.applyFromRoute();
  }

  /**
   * Permite a un componente (ej. la vista de una publicación) fijar el SEO con datos
   * reales cargados de forma async, en vez del data.seo estático de la ruta.
   * La ruta debe declarar `data: { seoDynamic: true }` para que applyFromRoute() no lo pise.
   */
  setDynamic(seo: SeoData, jsonLd?: Record<string, any>): void {
    this.applySeoData(seo);
    this.setJsonLd(jsonLd);
  }

  private applyFromRoute(): void {
    const { seo, seoDynamic } = this.collectRouteData();

    if (seoDynamic) {
      // El componente activo es responsable de llamar a setDynamic() con el contenido real.
      return;
    }

    this.applySeoData(seo);
    this.setJsonLd(undefined);
  }

  private applySeoData(seo: SeoData): void {
    // TITLE
    if (seo.title) this.titleSrv.setTitle(seo.title);

    // DESCRIPTION
    if (seo.description) {
      this.meta.updateTag({ name: 'description', content: seo.description });
    }

    // METAS ADICIONALES (og:*, twitter:*, etc.)
    if (seo.meta?.length) {
      for (const m of seo.meta) this.meta.updateTag(m);
    }

    // CANONICAL
    const path = (seo.canonicalPath ?? this.currentPath()) || '/';
    const canonicalHref = this.absUrl(path);
    this.setCanonical(canonicalHref);

    // og:url en sincronía (opcional, ayuda en social)
    this.meta.updateTag({ property: 'og:url', content: canonicalHref });

    // ROBOTS
    const robots = this.computeRobots(seo);
    // Limpia duplicados y setea uno solo
    this.meta.removeTag('name="robots"');
    this.meta.updateTag({ name: 'robots', content: robots });
  }

  // Combina data.seo y data.seoDynamic desde la ruta activa más profunda
  private collectRouteData(): { seo: SeoData; seoDynamic: boolean } {
    let r: ActivatedRoute | null = this.route;
    let seo: SeoData = {};
    let seoDynamic = false;
    // Sube/descendiendo por el árbol de rutas activas
    while (r) {
      if (r.snapshot?.data?.['seo']) {
        // El más profundo pisa a los padres
        seo = { ...seo, ...r.snapshot.data['seo'] as SeoData };
      }
      if (r.snapshot?.data?.['seoDynamic'] !== undefined) {
        seoDynamic = !!r.snapshot.data['seoDynamic'];
      }
      r = r.firstChild!;
    }
    return { seo, seoDynamic };
  }

  private setJsonLd(data: Record<string, any> | undefined): void {
    const existing = this.jsonLdEl ?? this.doc.querySelector<HTMLScriptElement>('script[data-seo-jsonld]');

    if (!data) {
      existing?.remove();
      this.jsonLdEl = null;
      return;
    }

    this.jsonLdEl = existing ?? this.doc.createElement('script');
    this.jsonLdEl.setAttribute('type', 'application/ld+json');
    this.jsonLdEl.setAttribute('data-seo-jsonld', '');
    this.jsonLdEl.textContent = JSON.stringify(data);

    if (!this.jsonLdEl.isConnected) {
      this.doc.head.appendChild(this.jsonLdEl);
    }
  }

  private currentPath(): string {
    // Ruta sin querystring
    return (this.router.url || '/').split('?')[0];
  }

  private absUrl(path: string): string {
    const base = (environment.baseUrl || '').replace(/\/$/, ''); // sin slash final
    const p = path.startsWith('/') ? path : `/${path}`;
    return `${base}${p}`;
  }

  private ensureCanonicalEl(): HTMLLinkElement {
    if (!this.canonicalEl) {
      const existing = this.doc.querySelector<HTMLLinkElement>('link[rel="canonical"]');
      this.canonicalEl = existing ?? this.doc.createElement('link');
      this.canonicalEl.setAttribute('rel', 'canonical');
      if (!existing) this.doc.head.appendChild(this.canonicalEl);
    }
    return this.canonicalEl;
  }

  private setCanonical(href: string): void {
    const el = this.ensureCanonicalEl();
    el.setAttribute('href', href);
  }

  private computeRobots(seo: SeoData): string {
    // En entornos NO prod, bloquea indexación global por seguridad
    if (!environment.production) return 'noindex, nofollow';

    // En producción: por defecto no indexa; solo indexa si lo marcas
    return seo?.index ? 'index, follow' : 'noindex, follow';
  }
}
